import { useMutationState } from '@tanstack/react-query';

import { TASK_MUTATION_KEYS } from './taskMutationKeys';

/** The shared prefix of every key in `TASK_MUTATION_KEYS`. */
const TASK_MUTATION_PREFIX = [TASK_MUTATION_KEYS.move[0]];

/** What a task is waiting on, in the words its card shows. */
export type PendingVerb =
  'moving' | 'stopping' | 'rerunning' | 'hiding' | 'deleting' | 'splitting';

const VERB_BY_KEY: Record<string, PendingVerb> = {
  move: 'moving',
  stop: 'stopping',
  rerun: 'rerunning',
  hide: 'hiding',
  delete: 'deleting',
  split: 'splitting',
};

/** The text a busy control shows while its request is in flight. */
export const PENDING_LABEL: Record<PendingVerb, string> = {
  moving: 'Moving…',
  stopping: 'Stopping…',
  rerunning: 'Starting…',
  hiding: 'Hiding…',
  deleting: 'Deleting…',
  splitting: 'Moving…',
};

/**
 * Every task with an action in flight, wherever it was started (spec 125
 * FR-008, research R3). Read from the mutation cache rather than copied into a
 * store: the cache already knows, and a copy could disagree with it.
 */
export function usePendingTaskActions(): ReadonlyMap<string, PendingVerb> {
  const entries = useMutationState({
    filters: { mutationKey: TASK_MUTATION_PREFIX, status: 'pending' },
    select: (mutation) => {
      const key = mutation.options.mutationKey?.[1];
      const variables = mutation.state.variables as
        { taskId?: string } | undefined;
      const verb = typeof key === 'string' ? VERB_BY_KEY[key] : undefined;
      return variables?.taskId && verb
        ? ([variables.taskId, verb] as const)
        : null;
    },
  });
  const map = new Map<string, PendingVerb>();
  for (const entry of entries) if (entry) map.set(entry[0], entry[1]);
  return map;
}

/** The verb one task is waiting on, or `undefined`. */
export function usePendingTaskAction(taskId: string): PendingVerb | undefined {
  return usePendingTaskActions().get(taskId);
}
